import { computed, defineComponent, PropType, reactive, ref } from 'vue';
import { useSweetAlert } from '@/composables/sweetalert';
import { useStore } from 'vuex';
import { AssignmentAction, AssignmentGetter } from '@/store/assignment/assignment.module';
import { AssignmentSchema } from '@/store/assignment/assignment.validator';
import { MinisterGetter } from '@/store/minister/minister.module';
import { Minister } from '@/store/minister/minister.model';
import { RegionGetter } from '@/store/region/region.module';
import { Region } from '@/store/region/region.model';
import { ProvinceGetter } from '@/store/province/province.module';
import { Province } from '@/store/province/province.model';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';
import Multiselect from '@vueform/multiselect';

export default defineComponent({
  name: 'AssignmentFormComponent',
  components: {
    BaseModalComponent,
    Multiselect,
  },
  props: {
    mode: {
      type: [String as () => 'ผู้แทน ร.นรม./รมต.นร' | 'ผู้ช่วยผู้ตรวจราชการ' | 'เจ้าหน้าที่ประจำจังหวัด'],
      default: () => 'เจ้าหน้าที่ประจำจังหวัด',
    },
    selectedUser: {
      type: Object as PropType<{
        id: string,
        name: string,
        assignmentItems: number[]
      }>,
    }
  },
  setup: (props) => {
    const swal = useSweetAlert();
    const store = useStore();
    const ministers = computed<Minister[]>(() => store.getters[MinisterGetter.ALL]);
    const regions = computed<Region[]>(() => store.getters[RegionGetter.ALL]);
    const provinces = computed<Province[]>(() => {
      const all = store.getters[ProvinceGetter.ALL] as Province[];
      return all.filter(m => m.iso !== 'TH-10');
    });
    const assignmentForm = ref<InstanceType<typeof BaseModalComponent>>();
    const assignmentOptions = ref<{ value: number, label: string }[]>([]);

    // form
    interface Form {
      mode?: string | null;
      id?: string | null;
      name?: string | null;
      assignmentItems?: number[];
    }
    const formData = reactive<Form>({
      mode: '',
      id: '',
      name: '',
      assignmentItems: [],
    });
    const validator = computed(() => AssignmentSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      assignmentItems: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'assignmentItems')),
    });
    const isValid = computed(() => !errors.assignmentItems);
    const isLoading = computed<boolean>(() => store.getters[AssignmentGetter.STATUS] === 'init' || store.getters[AssignmentGetter.STATUS] === 'loading');
    const isEditMode = computed<boolean>(() => props.selectedUser?.assignmentItems.length !== 0);
    const submited = ref(false);

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        formData.mode = props.mode != 'เจ้าหน้าที่ประจำจังหวัด'
          ? props.mode == 'ผู้แทน ร.นรม./รมต.นร'
            ? 'MINISTER'
            : 'REGION'
          : 'PROVINCE';
        formData.id = props.selectedUser?.id;
        formData.name = props.selectedUser?.name;
        formData.assignmentItems = props.selectedUser?.assignmentItems;

        assignmentOptions.value = props.mode != 'เจ้าหน้าที่ประจำจังหวัด'
          ? props.mode == 'ผู้แทน ร.นรม./รมต.นร'
            ? ministers.value.map(m => { return { value: m.id, label: m.fullName }; })
            : regions.value.map(m => { return { value: m.id, label: m.name }; })
          : provinces.value.map(m => { return { value: m.id, label: m.name }; });
        resolve();
      })
      .then(() => assignmentForm.value?.show());
    };

    // hide modal
    const hide = () => {
      assignmentForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.id = '';
        formData.name = '';
        formData.assignmentItems = [];
        assignmentOptions.value = [];
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        if (props.selectedUser?.assignmentItems.length !== 0) {
          store.dispatch(AssignmentAction.UPDATE, formData)
            .then(() => store.dispatch(AssignmentAction.FETCH, { mode: formData.mode })
              .then(() => hide()));
        } else {
          store.dispatch(AssignmentAction.CREATE, formData)
            .then(() => store.dispatch(AssignmentAction.FETCH, { mode: formData.mode })
              .then(() => hide()));
        }
      }
    };

    const deleteClick = () => {
      swal.confirm('ล้างข้อมูล!', 'คุณต้องการล้างข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            store.dispatch(AssignmentAction.DELETE, formData)
              .then(() => store.dispatch(AssignmentAction.FETCH, { mode: formData.mode })
                .then(() => hide()));
          }
        });
    };

    return {
      formData,
      assignmentForm,
      assignmentOptions,
      errors,
      submited,
      isLoading,
      isEditMode,
      show,
      hide,
      submitClick,
      deleteClick,
      unsubmit,
      clearForm,
    };
  },
});
